// src/pages/customer/About.jsx
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { getAboutContent, getCertificates } from "../../services/firestoreService";
import Spinner from "../../components/common/Spinner";
import { FiX, FiFileText } from "react-icons/fi";
import themeBg from "../../assests/theme.png";

// ── PDF helpers ──────────────────────────────────────────────────────────────
const isPDF = (url) => typeof url === "string" && url.toLowerCase().includes(".pdf");

const loadPdfJs = () =>
  new Promise((resolve, reject) => {
    if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    s.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
      resolve(window.pdfjsLib);
    };
    s.onerror = reject;
    document.head.appendChild(s);
  });

const renderPDFThumb = async (url) => {
  try {
    const lib  = await loadPdfJs();
    const pdf  = await lib.getDocument(url).promise;
    const page = await pdf.getPage(1);
    const vp   = page.getViewport({ scale: 1.5 });
    const canvas = document.createElement("canvas");
    canvas.width  = vp.width;
    canvas.height = vp.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
    return canvas.toDataURL();
  } catch { return null; }
};

function usePDFThumb(url) {
  const [thumb, setThumb] = useState(null);
  useEffect(() => {
    if (!isPDF(url)) return;
    renderPDFThumb(url).then(setThumb);
  }, [url]);
  return thumb;
}

// Build a Cloudinary "force download" URL.
// IMPORTANT: fl_attachment:<name> must NOT include the file extension —
// Cloudinary appends the original file's extension automatically.
// Including ".pdf" in the name causes a 400 "Bad Request" error.
const toDownloadUrl = (url, baseName) => {
  if (!url) return url;
  try {
    // Strip any extension from baseName (e.g. "FSSAI.pdf" → "FSSAI")
    const nameOnly = baseName.replace(/\.[^.]+$/, "");
    const safe = encodeURIComponent(nameOnly.replace(/[^a-zA-Z0-9_-]/g, "_"));
    return url.replace(/\/upload\/(?!fl_attachment)/, `/upload/fl_attachment:${safe}/`);
  } catch { return url; }
};

// Fetch the file as a blob and trigger a browser download.
// This works for both PDFs and images across all browsers.
const handleDownload = async (url, filename) => {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) throw new Error("fetch failed");
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch {
    // Fallback: open in new tab so user can save manually
    window.open(url, "_blank");
  }
};

function CertCard({ cert, onImageClick }) {
  const pdf   = isPDF(cert.image);
  const thumb = usePDFThumb(cert.image);
  const [downloading, setDownloading] = useState(false);

  // Card / image area click → open/view
  const handleView = () => {
    if (!cert.image) return;
    if (pdf) window.open(cert.image, "_blank");
    else onImageClick(cert.image);
  };

  // Extension to use for the saved file
  const ext = pdf ? ".pdf" : cert.image?.match(/\.(png|jpg|jpeg|webp)$/i)?.[0] || ".jpg";
  const saveFilename = cert.name + ext;

  // Download URL with fl_attachment (no extension in the transform name)
  const dlUrl = toDownloadUrl(cert.image, cert.name);

  const onDownload = async (e) => {
    e.stopPropagation();
    if (downloading || !cert.image) return;
    setDownloading(true);
    await handleDownload(dlUrl, saveFilename);
    setDownloading(false);
  };

  return (
    <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group flex flex-col">
      <div className="h-1.5 bg-gradient-to-r from-primary-500 to-leaf-600" />
      {/* Clickable image area → view */}
      <div
        className="flex-1 flex items-center justify-center p-8 bg-gray-50 cursor-pointer"
        onClick={handleView}
        title={pdf ? "Click to open PDF" : "Click to view image"}
      >
        <div className="relative">
          {cert.image ? (
            pdf ? (
              thumb ? (
                <img
                  src={thumb}
                  alt={cert.name}
                  className="w-28 h-28 object-cover rounded-2xl bg-white border border-gray-100 shadow-sm group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <div className="w-28 h-28 rounded-2xl bg-red-50 border border-red-100 flex flex-col items-center justify-center gap-1">
                  <FiFileText className="w-10 h-10 text-red-400" />
                  <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider">PDF</span>
                </div>
              )
            ) : (
              <img
                src={cert.image}
                alt={cert.name}
                className="w-28 h-28 object-contain rounded-2xl bg-white border border-gray-100 p-2 shadow-sm group-hover:scale-105 transition-transform duration-500"
              />
            )
          ) : (
            <div className="w-28 h-28 rounded-2xl bg-primary-50 flex items-center justify-center text-5xl">📜</div>
          )}
          <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold shadow">✓</div>
          {pdf && (
            <span className="absolute bottom-1 left-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase">PDF</span>
          )}
        </div>
      </div>
      {/* Content + single Download button */}
      <div className="px-6 pb-6 text-center">
        <h3 className="font-bold text-gray-900 text-base mb-1 line-clamp-2">{cert.name}</h3>
        {cert.issuedBy && <p className="text-xs text-gray-400 mb-5">{cert.issuedBy}</p>}
        <a
          href={dlUrl}
          download={saveFilename}
          onClick={onDownload}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors duration-200"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          {downloading ? "Downloading…" : "Download"}
        </a>
      </div>
    </div>
  );
}

const DEFAULT = {
  heroTitle:   "About Tripura Agro",
  heroSub:     "Connecting farmers to consumers with quality agricultural products.",
  mission:     "Our mission is to provide the freshest, highest-quality agricultural products directly from farms to your table, ensuring food security and supporting local farmers.",
  vision:      "We envision a world where fresh, nutritious farm produce is accessible to every household, while farmers receive fair compensation for their hard work.",
  whyUs:       "Tripura Agro Industries distinguishes itself through a diverse product range, stringent quality control, and a customer-centric approach, ensuring satisfaction and trust in every interaction.",
  whoWeAreTitle: "Who We Are",
  whoWeAreDesc:  "We are a team who understand crops, farmers, commodities and Taste.",
  whyChooseUs: [
    { icon: "🌿", title: "100% Organic",        desc: "All our products are organically grown without harmful pesticides." },
    { icon: "🚚", title: "Fast Delivery",        desc: "We deliver fresh produce to your doorstep within 24–48 hours." },
    { icon: "💰", title: "Competitive Prices",   desc: "Get the best quality produce at the most affordable prices." },
    { icon: "🤝", title: "Direct from Farmers",  desc: "We partner directly with farmers, cutting out the middlemen." },
  ],
  teamTitle: "Our Commitment",
  teamDesc:  "We are committed to building a sustainable agricultural supply chain that benefits both farmers and consumers. Every purchase you make supports local farming communities.",
};


const downloadCertificatePDF = (cert) => {
  try {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const pdf = new jsPDF("p", "mm", "a4");
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const iw = pw - 20;
      let ih = (img.height * iw) / img.width;
      if (ih > ph - 20) ih = ph - 20;
      pdf.addImage(img, "PNG", 10, 10, iw, ih);
      pdf.save(`${cert.name}.pdf`);
    };
    img.src = cert.image;
  } catch (e) {
    console.error(e);
  }
};

export default function About() {
  const [content,  setContent]  = useState(null);
  const [certs,    setCerts]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    Promise.all([getAboutContent(), getCertificates()]).then(([c, cs]) => {
      setContent({ ...DEFAULT, ...c });
      setCerts(cs);
      setLoading(false);
    });
  }, []);

  if (loading) return <Spinner center />;

  const why = Array.isArray(content.whyChooseUs) ? content.whyChooseUs : DEFAULT.whyChooseUs;

  return (
    <div className="min-h-screen bg-gray-50">

      <Helmet>
        <title>About Us | Tripura Agro</title>
        <meta
          name="description"
          content="Tripura Agro Industries was established in 2022 with the goal of leveraging supply and demand between farmers and Mandis at the national level and the final customer, who may be an individual or an institution."
        />
      </Helmet>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-leaf-600 to-primary-700 py-24 px-4 text-center text-white">
        {/* decorative rings */}
        <div className="pointer-events-none absolute -top-28 -left-28 w-[420px] h-[420px] rounded-full border border-white/10" />
        <div className="pointer-events-none absolute -bottom-20 -right-20 w-80 h-80 rounded-full border border-white/10" />
        <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-white/5" />

        <span className="inline-block text-xs font-semibold tracking-widest uppercase bg-white/20 px-4 py-1.5 rounded-full mb-5">
          Our Story
        </span>
        <h1 className="font-display text-4xl md:text-6xl font-bold mb-5 leading-tight" style={{ fontFamily: "var(--custom-body-font-family)" }}>
          {content.heroTitle}
        </h1>
        <p className="text-white/80 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
          {content.heroSub}
        </p>
      </section>

      {/* ── Shared styles ── */}
      <style>{`
        .about-label {
          display: inline-block;
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          font-family: Georgia, 'Times New Roman', serif;
          color: #4a6e30;
          margin-bottom: 8px;
        }
        .about-card-title {
          font-family: Georgia, 'Times New Roman', serif;
          font-size: 1.05rem;
          font-weight: 800;
          color: #1a2a0e;
          margin-bottom: 10px;
          line-height: 1.3;
        }
        .about-card-desc {
          font-size: 0.8rem;
          color: #5a6e48;
          line-height: 1.65;
        }
        .about-card {
          position: relative;
          overflow: hidden;
          background: #ffffff;
          border: 1.5px solid #d8e8c8;
          border-radius: 18px;
          padding: 28px 22px 26px;
          box-shadow: 0 2px 16px rgba(74,110,48,0.07);
          transition: box-shadow 0.25s, transform 0.25s;
          display: flex;
          flex-direction: column;
        }
        .about-card:hover {
          box-shadow: 0 8px 32px rgba(74,110,48,0.16);
          transform: translateY(-4px);
        }
        .about-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 3px;
          background: linear-gradient(90deg, #4a6e30, #8ab84a);
          border-radius: 18px 18px 0 0;
        }
        .about-icon-wrap {
          width: 46px;
          height: 46px;
          border-radius: 12px;
          background: #eef6e4;
          border: 1.5px solid #c2dba0;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
          flex-shrink: 0;
        }
        .about-divider {
          width: 32px;
          height: 2px;
          background: linear-gradient(90deg, #4a6e30, #8ab84a);
          border-radius: 2px;
          margin: 10px 0 12px;
        }
      `}</style>

      {/* ── 4 Boxes: Mission · Vision · Why Us · Who We Are ── */}
      <section className="relative overflow-hidden max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Decorative background illustration — sits behind the cards, centered */}
        <img
          src={themeBg}
          alt=""
          aria-hidden="true"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] sm:w-[760px] pointer-events-none select-none"
          style={{ opacity: 0.08, zIndex: 0 }}
        />

        <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" style={{ zIndex: 1 }}>

          {/* Mission */}
          <div className="about-card">
            <div className="about-icon-wrap">
              <svg width="22" height="22" style={{ color: "#4a6e30" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <span className="about-label">Mission</span>
            <div className="about-divider" />
            <h2 className="about-card-title">Our Mission</h2>
            <p className="about-card-desc">{content.mission}</p>
          </div>

          {/* Vision */}
          <div className="about-card">
            <div className="about-icon-wrap">
              <svg width="22" height="22" style={{ color: "#4a6e30" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <span className="about-label">Vision</span>
            <div className="about-divider" />
            <h2 className="about-card-title">Our Vision</h2>
            <p className="about-card-desc">{content.vision}</p>
          </div>

          {/* Why Choose Us */}
          <div className="about-card">
            <div className="about-icon-wrap">
              <svg width="22" height="22" style={{ color: "#4a6e30" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </div>
            <span className="about-label">Why Us</span>
            <div className="about-divider" />
            <h2 className="about-card-title">Why Choose Us</h2>
            <p className="about-card-desc">{content.whyUs}</p>
          </div>

          {/* Who We Are */}
          <div className="about-card">
            <div className="about-icon-wrap">
              <svg width="22" height="22" style={{ color: "#4a6e30" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <span className="about-label">Who We Are</span>
            <div className="about-divider" />
            <h2 className="about-card-title">{content.whoWeAreTitle}</h2>
            <p className="about-card-desc">{content.whoWeAreDesc}</p>
          </div>

        </div>
      </section>

      {/* ── Why Choose Us ── */}
      {/* <section className="bg-gray-50 py-20 px-4">
        <div className="max-w-6xl mx-auto sm:px-6 lg:px-8">

          <div className="text-center mb-14">
            <span className="inline-block text-xs font-semibold tracking-widest uppercase bg-primary-100 text-primary-700 px-4 py-1.5 rounded-full mb-4">
              Why us
            </span>
            <h2 className="section-title">Why Choose Us?</h2>
            <p className="text-gray-500 mt-3 max-w-md mx-auto text-sm">
              We bring the farm to your doorstep — fresh, honest, and always reliable.
            </p>
          </div>

          {(() => {
            const whyIcons = [
              // icon 0
              <svg key="0" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>,
              // icon 1
              <svg key="1" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>,
              // icon 2
              <svg key="2" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>,
              // icon 3
              <svg key="3" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>,
            ];
            const iconColors = [
              "bg-emerald-50 text-emerald-600",
              "bg-blue-50 text-blue-600",
              "bg-amber-50 text-amber-600",
              "bg-primary-50 text-primary-600",
            ];
            const cols = why.length === 3
              ? "grid-cols-1 sm:grid-cols-3"
              : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4";

            return (
              <div className={`grid ${cols} gap-6`}>
                {why.map((item, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group flex flex-col gap-4"
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${iconColors[i % iconColors.length]}`}>
                      {whyIcons[i % whyIcons.length]}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1.5 text-base">{item.title}</h3>
                      <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </section> */}

      {/* ── Commitment / Team Desc ── */}
      {(content.teamTitle || content.teamDesc) && (
        <section className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-leaf-600 to-primary-700 rounded-3xl p-10 md:p-14 text-center text-white relative overflow-hidden">
              {/* rings */}
              <div className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 rounded-full border border-white/10" />
              <div className="pointer-events-none absolute -bottom-10 -left-10 w-48 h-48 rounded-full border border-white/10" />

              <div className="relative z-10">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-6">
                  🌱
                </div>
                {content.teamTitle && (
                  <h2 className="font-display text-3xl font-bold mb-5">{content.teamTitle}</h2>
                )}
                {content.teamDesc && (
                  <p className="text-white/85 leading-relaxed text-base md:text-[15px] max-w-5xl mx-auto">
                    {content.teamDesc}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Certificates ── */}
      {certs.length > 0 && (
        <section className="bg-gray-50 py-20 px-4">
          <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold mb-4">
                🏆 Trusted &amp; Certified
              </span>
              <h2 className="font-display text-4xl font-bold text-gray-900 mb-4">Our Certifications</h2>
              <p className="max-w-2xl mx-auto text-gray-500 text-sm leading-relaxed">
                We maintain the highest quality standards and industry certifications
                to ensure trust, transparency, and excellence in every product.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-7">
              {certs.map((cert) => (
                <CertCard key={cert.id} cert={cert} onImageClick={setLightbox} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Image Lightbox ── */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox}
              alt="Certificate"
              className="max-w-full max-h-[85vh] rounded-2xl object-contain shadow-2xl"
            />
            <button
              onClick={() => setLightbox(null)}
              className="absolute -top-3 -right-3 bg-white text-gray-800 rounded-full p-1.5 shadow-lg hover:bg-gray-100 transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

    </div>
  );
}